import { config } from '@/config';
import { checkFeatureAccess, FEATURES } from '@/utils/permissions';

export const EMAIL_STATUSES = [
  { label: 'Sent', value: 'sent', color: '#b2bac6' },
  { label: 'Scheduled', value: 'scheduled', color: '#02Be4d' },
  { label: 'Delivered', value: 'delivered', color: '#00c5e4' },
  { label: 'Opened', value: 'opened', color: '#5300ff' },
  { label: 'Clicked', value: 'clicked', color: '#4caf50' },
  { label: 'Replied', value: 'replied', color: '#4caf50' },
  { label: 'Bounced', value: 'bounced', color: '#f9422d' },
  { label: 'Not Delivered', value: 'not_delivered', color: '#f9422d' }
];

// [label, tag, enabled(currentUser)] — tags without a condition work on every system
const TAGS = [
  ['Contact First Name', '#CONTACT_FIRST_NAME#'],
  ['Contact Last Name', '#CONTACT_LAST_NAME#'],
  ['Contact Phone', '#CONTACT_PHONE#'],
  ['Contact Company', '#CONTACT_COMPANY#', () => config().AGENTFLOW360],
  ['User First Name', '#USER_FIRST_NAME#'],
  ['User Last Name', '#USER_LAST_NAME#'],
  ['User Company', '#USER_COMPANY#'],
  ['User Phone', '#USER_PHONE#'],
  ['User Phone Ext', '#USER_PHONE_EXT#'],
  ['User Email', '#USER_EMAIL#'],
  ['User Address', '#USER_ADDRESS#'],
  ['User Website URL', '#USER_WEBSITE_URL#'],
  ['User Webpage URL', '#USER_WEBPAGE_URL#'],
  ['User Picture', '#USER_PICTURE#'],
  ['User Picture URL', '#USER_PICTURE_URL#'],
  ['User Corporation Logo', '#USER_CORPORATION_LOGO#'],
  ['User Corporation Logo URL', '#USER_CORPORATION_LOGO_URL#'],
  ['User Signature', '#USER_SIGNATURE#'],
  ['User Calendly Link', '#USER_CALENDLY_LINK#'],
  ['User Demio Link', '#USER_DEMIO_LINK#', () => config().AGENTFLOW360],
  ['Appointment Time', '#APPOINTMENT_TIME#'],
  ['Event Date', '#EVENT_DATE#'],
  ['Event Time', '#EVENT_TIME#'],
  ['Interview Type', '#INTERVIEW_TYPE#', currentUser => config(currentUser).AF360_BU11],
  ['Contact Note', '#CONTACT_NOTE#'],
  ['User COMRA Link', '#USER_COMRA_LINK#', currentUser => config(currentUser).COMRA_LINK],
  ['User RetireFlo Link', '#USER_RETIREFLO_LINK#', currentUser => config(currentUser).RETIREFLO_LINK],
  ['User Title', '#USER_TITLE#', currentUser => config(currentUser).USER_TITLE],
  ['Contact Office Location', '#CONTACT_OFFICE_LOCATION#', currentUser => config(currentUser).CONTACT_OFFICE_LOCATION],
  ['Contact Office Address', '#CONTACT_OFFICE_ADDRESS#', currentUser => config(currentUser).CONTACT_OFFICE_ADDRESS],
  ['User Plans Offered', '#USER_PLANS_OFFERED#', currentUser => !config(currentUser).PLANS_OFFERED_CARRIERS],
  ['User Carriers', '#USER_CARRIERS#', currentUser => !config(currentUser).PLANS_OFFERED_CARRIERS],
  ['User GLIC RX', '#USER_GLIC_RX#', currentUser => config(currentUser).GLIC_RX],
  ['User Google Review Link', '#USER_GOOGLE_REVIEW_LINK#', () => config().HRGAGENT],
  [
    'SOA Link',
    '#SOA_LINK#',
    currentUser => config(currentUser).SOA_REQUESTS && checkFeatureAccess(currentUser.permissions, FEATURES.SOA).active
  ],
  ['Integrity Connect Webpage Link', '#MEDICARE_LINK#', () => config().MEDICARE_CENTER],
  ['User Flyer', '#USER_FLYER_LINK#', () => config().FLYER_UPLOAD]
];

// The Content Center server builds templates for every system, so it offers every tag
function emailTags({ currentUser, isHTMLEditor } = {}) {
  const all = config().CONTENT_CENTER;

  const tags = TAGS.filter(([, , enabled]) => all || !enabled || enabled(currentUser));
  if (isHTMLEditor) return tags.map(([label, value]) => ({ [label]: value }));

  return tags.map(([label, value]) => ({ value, label }));
}

export const HTML_EDITOR_EMAIL_TAGS = ({ currentUser }) => emailTags({ currentUser, isHTMLEditor: true });
export const EMAIL_TAGS = ({ currentUser }) => emailTags({ currentUser });

const GENERAL_AUDIENCE_OPTIONS = [
  { label: 'Client', value: 'client' },
  { label: 'Lead', value: 'lead' },
  { label: 'Prospect', value: 'prospect' },
  { label: 'All/General', value: 'all' }
];

// CF360-only template metadata. Mirrors mad-api/app/models/concerns/email_template_attributes.rb.
const CF360_AUDIENCE_OPTIONS = [
  { label: 'Lead', value: 'lead' },
  { label: 'Prospect', value: 'prospect' },
  { label: 'Client', value: 'client' },
  { label: 'Other', value: 'other' },
  { label: 'All audiences', value: 'all' }
];

export const TEMPLATE_PRODUCT_TYPE_OPTIONS = [
  { label: 'Medicare', value: 'medicare' },
  { label: 'Life Insurance', value: 'life_insurance' },
  { label: 'Retirement Planning', value: 'retirement_planning' },
  { label: 'Supplemental Health', value: 'supplemental_health' },
  { label: 'Annuities', value: 'annuities' },
  { label: 'Generic', value: 'generic' }
];

export const TEMPLATE_INTENT_OPTIONS = [
  { label: 'Lead Gen', value: 'lead_gen' },
  { label: 'Appt Setting', value: 'appt_setting' },
  { label: 'Cross Sell', value: 'cross_sell' },
  { label: 'Retention', value: 'retention' },
  { label: 'Review', value: 'review' },
  { label: 'Nurture', value: 'nurture' },
  { label: 'Seasonal', value: 'seasonal' }
];

export const TEMPLATE_AUDIENCE_OPTIONS = config().CLIENTFLOW360 ? CF360_AUDIENCE_OPTIONS : GENERAL_AUDIENCE_OPTIONS;

export const TEMPLATE_CATEGORY_OPTIONS = [
  'Appointments',
  'Life Events & Milestones',
  'Client Retention & Appreciation',
  'Post-Sale',
  'Intro & Welcome',
  'Seasonal',
  'Re-Engagement',
  'Referrals'
].map(v => ({ label: v, value: v }));

export const TEMPLATE_REQUIREMENT_OPTIONS = ['Calendly Link', 'Google Referral Link', 'Event', 'Contact'].map(v => ({
  label: v,
  value: v
}));

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
